import { ThunderboltOutlined } from "@ant-design/icons"
import type { TFunction } from "i18next"
import { Button, Flex } from "antd"
import React, { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { Link, useSearchParams } from "react-router-dom"

import {
  STATUS_SUGGESTION_PARAM,
  blocksTheMove,
  eventDisplay,
  missingFieldsHref,
  statusDisplay
} from "../helpers/status-automation.ts"
import type {
  StatusRecordKind,
  StatusSuggestion
} from "../types/status-automation.types.ts"

export interface StatusSuggestionChipsProps {
  kind: StatusRecordKind
  recordId: string
  suggestions: StatusSuggestion[] | undefined
  /** Whether this user could make the move by hand; decided per suggestion. */
  canAct: (suggestion: StatusSuggestion) => boolean
  busyId?: string | null
  onAccept: (suggestion: StatusSuggestion) => void
  onDismiss: (suggestion: StatusSuggestion) => void
}

export function StatusSuggestionChips({
  kind,
  recordId,
  suggestions,
  canAct,
  busyId = null,
  onAccept,
  onDismiss
}: StatusSuggestionChipsProps) {
  if (!suggestions?.length) return null

  return (
    <Flex
      gap={8}
      wrap
      data-testid={"status-suggestions"}
      className={"py-2"}
    >
      {suggestions.map((suggestion) => (
        <StatusSuggestionChip
          key={suggestion.id}
          kind={kind}
          recordId={recordId}
          suggestion={suggestion}
          allowed={canAct(suggestion)}
          busy={busyId === suggestion.id}
          locked={!!busyId}
          onAccept={() => onAccept(suggestion)}
          onDismiss={() => onDismiss(suggestion)}
        />
      ))}
    </Flex>
  )
}

interface ChipProps {
  kind: StatusRecordKind
  recordId: string
  suggestion: StatusSuggestion
  allowed: boolean
  busy: boolean
  locked: boolean
  onAccept: () => void
  onDismiss: () => void
}

function StatusSuggestionChip({
  kind,
  recordId,
  suggestion,
  allowed,
  busy,
  locked,
  onAccept,
  onDismiss
}: ChipProps) {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const ref = useRef<HTMLDivElement>(null)
  const highlighted = searchParams.get(STATUS_SUGGESTION_PARAM) === suggestion.id

  useEffect(() => {
    if (highlighted) ref.current?.scrollIntoView?.({ block: "center" })
  }, [highlighted])

  const status =
    suggestion.target === null
      ? null
      : statusDisplay(kind, suggestion.target, t)
  const event = eventDisplay(
    suggestion.event,
    t,
    suggestion.source?.label,
    suggestion.source?.category
  )
  const isFlag = suggestion.target === null
  const blocked = blocksTheMove(suggestion)

  let sentence: React.ReactNode
  if (isFlag) {
    sentence = flagSentence(suggestion, event, t)
  } else if (blocked) {
    const fields = suggestion.missing_fields.length
      ? suggestion.missing_fields.join(", ")
      : suggestion.missing_field_names.join(", ")
    const addLabel = t("statusAutomation.chip.add", { fields })
    const href = missingFieldsHref(kind, recordId, suggestion)
    sentence = (
      <>
        {t("statusAutomation.chip.readyFor", { status })}
        {" — "}
        {allowed && href ? (
          <Link to={href} data-testid={"status-suggestion-missing-link"}>
            {addLabel}
          </Link>
        ) : (
          addLabel
        )}
      </>
    )
  } else {
    sentence = event
      ? t("statusAutomation.chip.moveAfterEvent", { event, status })
      : t("statusAutomation.chip.move", { status })
  }

  return (
    <div
      ref={ref}
      role={"group"}
      aria-label={t("statusAutomation.chip.label")}
      data-testid={`status-suggestion-${suggestion.id}`}
      data-highlighted={highlighted || undefined}
      className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-1.5 text-sm ${
        isFlag || blocked
          ? "border-amber-200 bg-amber-50"
          : "border-teal-200 bg-teal-50"
      } ${highlighted ? "ring-2 ring-teal-600 ring-offset-1" : ""}`}
    >
      <ThunderboltOutlined
        aria-hidden
        className={isFlag || blocked ? "text-amber-600" : "text-teal-700"}
      />
      <span data-testid={"status-suggestion-text"}>{sentence}</span>
      {allowed && (
        <Flex gap={4}>
          {!blocked && (
            <Button
              size={"small"}
              type={"primary"}
              loading={busy}
              disabled={locked && !busy}
              onClick={onAccept}
            >
              {isFlag
                ? t("statusAutomation.chip.acknowledge")
                : t("statusAutomation.chip.moveAction")}
            </Button>
          )}
          {!isFlag && (
            <Button
              size={"small"}
              type={"text"}
              disabled={locked}
              onClick={onDismiss}
            >
              {t("statusAutomation.chip.dismiss")}
            </Button>
          )}
        </Flex>
      )}
    </div>
  )
}

const flagSentence = (
  suggestion: StatusSuggestion,
  event: string | null,
  t: TFunction
): string => {
  if (
    suggestion.rule === "project.blocked" ||
    suggestion.reason === "milestone_blocked"
  ) {
    return t("statusAutomation.chip.blocked")
  }
  if (suggestion.rule === "lead.stale") return t("statusAutomation.chip.stale")
  return event ?? t("statusAutomation.chip.flag")
}
